// Explanations shown in the ⓘ tooltip next to table column headers
// (components/ColumnInfo.tsx). Grouped by table, keyed by the column key the
// table uses. A column without an entry simply shows no ⓘ.

/** Adds each column's `description` from `descriptions` (by column key). */
export function withDescriptions<T extends { key: string }>(
  columns: T[],
  descriptions: Record<string, string>,
): (T & { description?: string })[] {
  return columns.map((column) => ({ ...column, description: descriptions[column.key] }))
}

export const CLIENT_COLUMNS: Record<string, string> = {
  created_at: 'The date the client signed up in the app.',
  user_name: 'The name on the client\'s account.',
  email: 'The email address the client signs in with.',
  clientId: 'The client ID your business assigned to this client.',
  status:
    'Whether the client currently has access, and how. "Active" means a free trial, a monthly or annual subscription, or a licence that hasn\'t expired. "Inactive" means no subscription, or a subscription or licence that was canceled or has expired.',
  last_seen: 'The last time the client opened the app.',
  expire_at: 'The date the client\'s current subscription or licence ends.',
  business_name: 'The business this client belongs to.',
}

export const PENDING_CLIENT_COLUMNS: Record<string, string> = {
  pending_user_name:
    'The name entered when the client was added. Pending clients were sent an access code but haven\'t signed up in the app yet.',
  pending_email: 'The email address the access code was sent to.',
  clientId: 'The client ID assigned to this client when they were added.',
  business_name: 'The business this client belongs to.',
}

export const CLIENT_ID_COLUMNS: Record<string, string> = {
  clientId: 'The client ID itself, as your business assigns it.',
  type: '"Individual ID" is for one client. "Business ID" can be shared by several clients, up to its limit.',
  usage: 'Clients using this ID / the most clients allowed to use it.',
  name: 'The client who uses this ID.',
  businessId: 'The business this client ID belongs to.',
}

export const ADVISOR_COLUMNS: Record<string, string> = {
  user_name: 'The name on the advisor\'s account.',
  email: 'The email address the advisor signs in with.',
  status:
    'Whether the advisor currently has access, and how. "Active" means a free trial, a monthly or annual subscription, or a licence that hasn\'t expired. "Inactive" means no subscription, or a subscription or licence that was canceled or has expired.',
  created_at: 'The date the advisor signed up in the app.',
  expire_at: 'The date the advisor\'s current subscription or licence ends.',
  business_name: 'The business this advisor belongs to.',
}

export const PENDING_ADVISOR_COLUMNS: Record<string, string> = {
  pending_user_name:
    'The name entered when the advisor was added. Pending advisors haven\'t signed up in the app yet.',
  pending_email: 'The email address the invitation was sent to.',
}

export const CONTRACT_COLUMNS: Record<string, string> = {
  contract_ref: 'The contract reference.',
  expire_at: 'The date the contract ends.',
  useLimit: 'How many licences the contract includes.',
  licence_count: 'How many of those licences have been issued so far.',
  status: 'Whether the contract is still in effect.',
}

export const BUSINESS_COLUMNS: Record<string, string> = {
  name: 'The business name.',
  code: 'The business\'s short code.',
  type: 'The kind of business, for example a white-label partner or the owner.',
  enableAdvisors: 'Whether this business\'s clients can be linked to advisors.',
  needClientId: 'Whether every client of this business needs a client ID.',
  needAdminDashboard: 'Whether this business has access to this admin dashboard.',
}

export const REFERRAL_CODE_COLUMNS: Record<string, string> = {
  prefix: 'The tag or campaign name the referral code was created for.',
  useLimit: 'The most times the code can be used.',
  useCount: 'How many times the code has been used so far.',
  expire_at: 'The date after which the code can no longer be used.',
  status: 'Whether the code can still be used.',
}

// Access codes table headers, in display order (access codes use
// CustomTableRow, whose headers are a plain list of labels).
export const ACCESS_CODE_COLUMNS: Record<string, string> = {
  Type: 'Who the code is for: a client or an advisor.',
  'Licence Access Code':
    'The code itself. Referral codes show #code with the business they were issued for in brackets.',
  'Expiry Date': 'The date after which the code can no longer be used.',
  Status:
    '"Active": not used yet and still valid. "Validated": already used to sign up. "Expired": its expiry date has passed.',
}

export const ERROR_COLUMNS: Record<string, string> = {
  firstSeen: 'When this error first happened.',
  lastSeen: 'When this error last happened.',
  errorMessage: 'The error message reported by the app.',
  occurrences: 'How many times the error has happened in total.',
  affectedUsersCount: 'How many different users have hit this error.',
  status: '"Open": not looked at yet. "In Progress": being worked on. "Resolved": fixed.',
  priority: 'How urgent the error is: High, Medium or Low.',
}

export const FINANCIAL_INSTITUTION_COLUMNS: Record<string, string> = {
  providerName: 'The bank or financial institution clients can connect to.',
  country: 'The country the institution operates in.',
  updatedAt: 'When this institution\'s details were last updated.',
  status: 'Whether clients can currently connect to this institution.',
}
